import { ApolloClient } from 'apollo-client'
import { HttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloLink, from, Observable } from 'apollo-link';
import { onError } from 'apollo-link-error'
import { Toast } from 'antd-mobile'
import {
  ensureSession,
  getSessionToken,
  pickPatientId,
  isLoginRequiredError,
  isSessionRejected,
  forgetSessions,
  SESSION_HEADER,
} from './h5Session'
import { requestLogin } from './loginGate'

const syncGet = url => {
  const xmlhttp = new XMLHttpRequest()
  xmlhttp.open('GET', url, false)
  xmlhttp.send(null)
  if (xmlhttp.status === 200) {
    return xmlhttp.responseText
  }
}

const getUri = () => {
  if (process.env.NODE_ENV === 'production') {
    const http = '/_http_server_uri_'
    return syncGet(http)
  }
  return 'http://localhost:3181/graphql'
}

const uri = getUri()

// 登录框需要直接调 GraphQL（不经 Apollo 链路，避免被登录拦截器再次拦住）
export const graphqlUri = uri

const httpLink = new HttpLink({
  uri,
})

// 首个带 patientId 的请求发出前先换票，之后所有请求带上会话头。
// 后端写接口以会话为准、忽略请求参数里的身份，避免被冒充（见 common/h5Session.js）
const sessionLink = new ApolloLink((operation, forward) => {
  if (operation.operationName === 'CreateH5Session') return forward(operation)
  const patientId = pickPatientId(operation.variables)
  return new Observable((observer) => {
    let subscription
    let healed = false
    const send = () => {
      const token = getSessionToken()
      operation.setContext(({ headers = {} }) => {
        const next = { ...headers }
        if (token) next[SESSION_HEADER] = token
        else delete next[SESSION_HEADER]
        return { headers: next }
      })
      subscription = forward(operation).subscribe({
        next: (result) => {
          // 服务端不认手里的会话：丢掉本地会话、重新换票后重发一次（只重试一次）
          if (!healed && isSessionRejected(result)) return heal(() => observer.next(result))
          observer.next(result)
        },
        error: (err) => {
          if (!healed && isSessionRejected(err)) return heal(() => observer.error(err))
          observer.error(err)
        },
        complete: () => {
          if (!healing) observer.complete()
        },
      })
    }
    let healing = false
    const heal = (fallback) => {
      healed = true
      healing = true
      forgetSessions()
      ensureSession(uri, patientId).then(
        (token) => {
          healing = false
          if (!token) return fallback(), observer.complete()
          send()
        },
        () => {
          healing = false
          fallback()
          observer.complete()
        }
      )
    }
    // 换票失败不阻断业务：阅读类接口在未开启强制时不要求会话
    ensureSession(uri, patientId).then(send, send)
    return () => subscription && subscription.unsubscribe()
  })
})

const middlewareHeaderLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      'client-codename': 'H5-BLOGS',
      'Access-Control-Allow-Origin': '*',
    },
  })
  return forward(operation)
})

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>{
      if (message){
        const arr = message.split('###')
        if (arr && arr.length > 1){
          if (arr[0] === 'ERROR-1010') {
            Toast.info('内容中包含不合规信息，请修改',2)
          } else {
            Toast.info(arr[1], 2)
          }
        }
      }
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      )
    }
    );
  }
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

// 写操作被后端要求登录时弹框，登录成功后原样重放该请求（业务代码不感知）
const LOGIN_REQUIRED = 'H5_LOGIN_REQUIRED'

const withSessionHeader = (operation) =>
  operation.setContext(({ headers = {} }) => {
    const token = getSessionToken()
    return { headers: token ? { ...headers, [SESSION_HEADER]: token } : headers }
  })

const needsLogin = (result) =>
  ((result && result.errors) || []).some((e) => String(e.message || '').indexOf(LOGIN_REQUIRED) >= 0)

// 登录后重放成功时调用；client 在下方创建，故延迟取用
let refreshAfterLogin = () => {}

const loginGateLink = new ApolloLink((operation, forward) =>
  new Observable((observer) => {
    let sub
    let retried = false
    // 等待用户登录期间不能把 complete 透传出去：一旦 complete，
    // 上层就会结束订阅，重放的结果再也送不到业务层（此前评论弹不出登录框即因此）
    let waitingLogin = false

    const retryAfterLogin = (fallback) => {
      waitingLogin = true
      requestLogin().then(
        () => {
          waitingLogin = false
          withSessionHeader(operation)
          sub = forward(operation).subscribe({
            next: (result) => {
              observer.next(result)
              // 重放成功后刷新页面上的查询：点赞数、评论列表等界面状态
              // 在失败时已被本地乐观更新回滚过，需要和服务端重新对齐
              refreshAfterLogin()
            },
            error: (e) => observer.error(e),
            complete: () => observer.complete(),
          })
        },
        () => {
          // 用户取消登录：把原结果交回业务，由既有提示处理
          waitingLogin = false
          fallback()
        }
      )
    }

    sub = forward(operation).subscribe({
      next: (result) => {
        if (retried || !needsLogin(result)) {
          observer.next(result)
          return
        }
        retried = true
        retryAfterLogin(() => {
          observer.next(result)
          observer.complete()
        })
      },
      error: (err) => {
        if (retried || !isLoginRequiredError(err)) {
          observer.error(err)
          return
        }
        retried = true
        retryAfterLogin(() => observer.error(err))
      },
      complete: () => {
        if (!waitingLogin) observer.complete()
      },
    })

    return () => sub && sub.unsubscribe()
  })
)

const link = ApolloLink.from([
  middlewareHeaderLink,
  sessionLink,
  loginGateLink,
  errorLink,
  httpLink,
]);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
})

refreshAfterLogin = () => {
  try {
    client.reFetchObservableQueries()
  } catch (e) {
    // 刷新失败不影响已经成功的写操作
  }
}

export default client



// WEBPACK FOOTER //
// ./src/common/ApolloClient.js